//#region types
import { AuthEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { InvoiceConstants } from "utils/constants";
import useAPIClientHelper from "./useAPIClientHelper";
import { DateHelper, EnumHelper, InvoiceHelper, LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
//#endregion

function useRecurringInvoiceService() {
  const { apiClientInstance } = useAPIClientHelper();

  const get = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RecurringInvoiceClient.GetById(id)
      .then((res) => res.data);
  };

  const filter = (filter: WintSuperkollAPIModel.RecurringInvoiceFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).RecurringInvoiceClient.GetByFilter(filter);
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: InvoiceFormTypes.RecurringInvoiceFilter) => {
    return filter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: InvoiceFormTypes.RecurringInvoiceFilter
  ): Omit<WintSuperkollAPIModel.RecurringInvoiceFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      ownerIds: formFilter?.ownerId ? [formFilter.ownerId] : [],
      customerIds: formFilter?.customerId ? [formFilter.customerId] : [],
      firstInvoiceDateFrom: formFilter?.startDate?.start,
      firstInvoiceDateTo: formFilter?.startDate?.end,
      endDateFrom: formFilter?.endDate?.start,
      endDateTo: formFilter?.endDate?.end,
      numberOfSentInvoicesFrom: formFilter?.numOfSentInvoices?.lowest,
      numberOfSentInvoicesTo: formFilter?.numOfSentInvoices?.highest,
      minTotalAmount: formFilter?.amount?.lowest,
      maxTotalAmount: formFilter?.amount?.highest,
      totalIncomeFrom: formFilter?.income?.lowest,
      totalIncomeTo: formFilter?.income?.highest,
      states: formFilter?.status?.map((s) => s.id as WintSuperkollAPIModel.RecurringInvoiceState),
      frequencies: formFilter?.frequency?.map((s) => s.id as WintSuperkollAPIModel.RecurringInvoiceFrequency),
      recurringInvoiceSendType: formFilter?.sendType?.map(
        (s) => s.id as WintSuperkollAPIModel.RecurringInvoiceSendType
      ),
    };
  };

  const createOrUpdateInvoice = async (
    id?: number,
    invoice?: WintSuperkollAPIModel.RecurringInvoicePut
  ): Promise<number | undefined> => {
    let invoiceId = id;
    if (invoice)
      if (id) await updateInvoice(id, invoice);
      else {
        invoiceId = await createInvoice(invoice);
      }

    return invoiceId;
  };

  const createInvoice = (invoicePost: WintSuperkollAPIModel.RecurringInvoicePut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .RecurringInvoiceClient.Post(invoicePost)
      .then((res) => res.data);
  };

  const updateInvoice = (invoiceId: number, invoice: WintSuperkollAPIModel.RecurringInvoicePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).RecurringInvoiceClient.Update(
      invoiceId,
      invoice
    );
  };

  const getCopiedRecurringInvoice = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RecurringInvoiceClient.GetCopiedRecurringInvoice(invoiceId)
      .then((res) => res.data);
  };

  const start = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).RecurringInvoiceClient.Start(invoiceId);
  };

  const stop = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).RecurringInvoiceClient.Stop(invoiceId);
  };

  const convertFormToAPIRequest = (
    form: InvoiceFormTypes.Create,
    noVatCompany: boolean
  ): WintSuperkollAPIModel.RecurringInvoicePut => {
    const serialNumberTemplateValueDisplayText = LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Invoice.InvoiceNumberHashTag
    );

    const recurringPut: WintSuperkollAPIModel.RecurringInvoicePut = {
      ownerId: form.recurringSettings.recurringOwner.id as number,
      customerId: (form.customer?.id as number) ?? 0,
      customerReference: form.customer?.id
        ? {
            referenceId: form.referencePerson?.id as number,
            referenceName: form.referencePerson?.value,
          }
        : undefined,
      rows: form.products?.map((product, index) => {
        const periodisation = form.isPeriodisationAdvancedConfiguration
          ? product.recurringPeriodisation
          : form.products![0].recurringPeriodisation;

        return {
          guid: product.guid,
          id: product.id,
          articleId: product.article?.id as number | undefined,
          orderIndex: index,
          quantity: product.quantity,
          unit: product.unit ? { id: product.unit } : { id: 0 },
          unitPrice: product.price ?? 0,
          vat: product.tax,
          overriddenVat: noVatCompany ? 0 : product.tax,
          description: product.article?.value ?? "",
          overriddenDescription: product.text,
          overriddenUnitPrice: product.price ?? 0,
          euVatProductType: product.eUVatCategoryId,
          periodisation: !!periodisation?.isEnabled,
          periodisationLength: periodisation?.duration ?? 0,
          periodisationStartOffset: periodisation?.from ?? 0,
          dimensions: form.isDimensionsAdvancedConfiguration ? product.dimensions : [],
          rut: false,
          rot: false,
        };
      }),
      contactPerson: form.otherSettings.sellerName,
      notes: form.otherSettings.notes,
      dimensions: form.isDimensionsAdvancedConfiguration
        ? []
        : form.products
          ? (form.products[0]?.dimensions ?? [])
          : [],
      showReferencePersonInAddress: !!form.otherSettings.showReferenceInAddress,
      showShippingAddress: !!form.otherSettings.showCustomerDeliveryAddress,
      showCurrencyCode: !!form.otherSettings.showCurrencyCode,
      language: form.otherSettings.languageKey,
      currency: form.currency,
      paymentOptionId: form.otherSettings.paymentMethodId,
      attachments: form.otherSettings.attachments as WintSuperkollAPIModel.InvoiceAttachment[],
      template: form.otherSettings.invoiceLayout as WintSuperkollAPIModel.InvoicePdfTemplate,
      recurringDescription: form.recurringSettings.recurringDescription,
      invoiceFrequency: form.recurringSettings.recurringInvoiceFrequency,
      firstPostingDate: form.recurringSettings.recurringInvoiceStartDate,
      untilFurtherNotice: !!form.recurringSettings.recurringRepeatUntilNotice,
      stopDate: form.recurringSettings.recurringRepeatUntilNotice
        ? undefined
        : form.recurringSettings.recurringInvoiceEndDate,
      daysToDelivery: form.recurringSettings.recurringDeliveryAfterDays,
      paymentTerms: form.otherSettings.paymentTerms ?? 0,
      recurringInvoiceSendType: form.recurringSettings.recurringInvoiceSendType,
      invoiceSendType: form.recurringSettings.recurringInvoiceAutomaticSendType,
      mailFromType:
        form.recurringSettings.recurringInvoiceSendType === WintSuperkollAPIModel.RecurringInvoiceSendType.Auto &&
        form.recurringSettings.recurringInvoiceAutomaticSendType === WintSuperkollAPIModel.InvoiceSendType.SendByEmail
          ? form.recurringSettings.recurringSendMail?.emailFromType ===
            WintSuperkollAPIModel.SendInvoiceMailsFromEnum.CompanyMail
            ? WintSuperkollAPIModel.SendRecurringInvoiceMailsFromEnum.CompanyMail
            : WintSuperkollAPIModel.SendRecurringInvoiceMailsFromEnum.Superkoll
          : WintSuperkollAPIModel.SendRecurringInvoiceMailsFromEnum.Superkoll,
      mailToType:
        form.recurringSettings.recurringInvoiceSendType === WintSuperkollAPIModel.RecurringInvoiceSendType.Auto &&
        form.recurringSettings.recurringInvoiceAutomaticSendType === WintSuperkollAPIModel.InvoiceSendType.SendByEmail
          ? InvoiceHelper.getEmailToType(
              form.mailOptions?.defaultEmailTo,
              form.recurringSettings.recurringSendMail.emailTo
            )
          : WintSuperkollAPIModel.SendInvoiceMailsToEnum.CustomerCompany,
      mailTo:
        form.recurringSettings.recurringInvoiceSendType === WintSuperkollAPIModel.RecurringInvoiceSendType.Auto &&
        form.recurringSettings.recurringInvoiceAutomaticSendType === WintSuperkollAPIModel.InvoiceSendType.SendByEmail
          ? form.recurringSettings.recurringSendMail?.emailTo
              ?.map((to) => to?.value)
              .filter((email) => email)
              .join(",")
          : undefined,
      mailTitle: form.recurringSettings.recurringSendMail?.emailSubject?.replace(
        serialNumberTemplateValueDisplayText,
        InvoiceConstants.SerialNumberTemplateValue
      ),
      mailMessage: form.recurringSettings.recurringSendMail?.emailMessage,
    };

    return recurringPut;
  };

  const convertFormToPreviewRequest = (
    form: InvoiceFormTypes.Create,
    noVatCompany: boolean,
    hideDeliveryDate: boolean,
    invoice?: WintSuperkollAPIModel.RecurringInvoice
  ) => {
    const invoicePut: WintSuperkollAPIModel.InvoicePreviewRequest = {
      ownerId: form.ownerId,
      attachForwardedCostDocuments: false,
      customerId: (form.customer?.id as number) ?? 0,
      customerReference: form.customer?.id
        ? {
            referenceId: form.referencePerson?.id as number,
            referenceName: form.referencePerson?.value,
          }
        : undefined,
      rows: form.products?.map((product, index) => {
        const periodisation = form.isPeriodisationAdvancedConfiguration
          ? product.recurringPeriodisation
          : form.products![0].recurringPeriodisation;

        let firstPeriodisation, firstPeriodisationEnd;
        if (periodisation?.from !== undefined && periodisation.duration !== undefined) {
          firstPeriodisation = DateHelper.addMonths(
            form.recurringSettings.recurringInvoiceStartDate,
            periodisation?.from
          );
          firstPeriodisationEnd = DateHelper.addMonths(firstPeriodisation, periodisation?.duration - 1);
        }

        return {
          id: product.id ?? 0,
          articleId: Number(product.article?.id ?? 0),
          orderIndex: index,
          quantity: Number(product.quantity),
          unit: product.unit ? { id: product.unit } : { id: 0 },
          unitPrice: Number(product.price ?? 0),
          vat: Number(product.tax),
          overriddenVat: Number(noVatCompany ? 0 : product.tax),
          description: product.article?.value ?? "",
          overriddenDescription: product.text,
          overriddenUnitPrice: Number(product.price ?? 0),
          euVatProductType: product.eUVatCategoryId,
          periodisation: !!product.recurringPeriodisation?.isEnabled,
          periodisationStartMonth: firstPeriodisation && DateHelper.getMonth(firstPeriodisation),
          periodisationStartYear: firstPeriodisation && DateHelper.getYear(firstPeriodisation),
          periodisationEndMonth: firstPeriodisationEnd && DateHelper.getMonth(firstPeriodisationEnd),
          periodisationEndYear: firstPeriodisationEnd && DateHelper.getYear(firstPeriodisationEnd),
          dimensions: form.isDimensionsAdvancedConfiguration ? product.dimensions : [],
          rut: false,
          rot: false,
        };
      }),
      contactPerson: form.otherSettings.sellerName,
      notes: form.otherSettings.notes,
      dimensions: form.isDimensionsAdvancedConfiguration
        ? []
        : form.products
          ? (form.products[0]?.dimensions ?? [])
          : [],
      deliveryDate:
        !hideDeliveryDate && form.recurringSettings.recurringDeliveryAfterDays !== undefined
          ? DateHelper.addDays(
              form.recurringSettings.recurringInvoiceStartDate,
              form.recurringSettings.recurringDeliveryAfterDays
            )
          : form.recurringSettings.recurringInvoiceStartDate,
      postingDate: form.recurringSettings.recurringInvoiceStartDate,
      dueDate: DateHelper.addDays(
        form.recurringSettings.recurringInvoiceStartDate,
        form.otherSettings.paymentTerms ?? 0
      ),
      showReferencePersonInAddress: !!form.otherSettings.showReferenceInAddress,
      showShippingAddress: !!form.otherSettings.showCustomerDeliveryAddress,
      showCurrencyCode: !!form.otherSettings.showCurrencyCode,
      language: form.otherSettings.languageKey ?? "",
      currency: form.currency,
      paymentOptionId: form.otherSettings.paymentMethodId,
      template: form.otherSettings.invoiceLayout as WintSuperkollAPIModel.InvoicePdfTemplate,
      useStrictValidation: true,
      recurringInvoiceId: invoice?.id ?? 0,
      recurringInvoiceVersion:
        invoice?.recurringInvoiceVersion ?? WintSuperkollAPIModel.RecurringInvoiceVersion.V1_PeriodInfo,
      invoiceRotPropertyType: WintSuperkollAPIModel.InvoiceRotPropertyType.None,
      invoiceHouseWorkType: WintSuperkollAPIModel.InvoiceHouseWorkType.None,
      invoiceSource: WintSuperkollAPIModel.InvoiceSource.Unknown,
      invoiceRotPercentageType: WintSuperkollAPIModel.InvoiceRotPercentageType.Standard,
    };

    return invoicePut;
  };

  const getFrequencyText = (frequency: WintSuperkollAPIModel.RecurringInvoiceFrequency) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringFrequency_${frequency}`);
  };
  const getSendTypeText = (type: WintSuperkollAPIModel.RecurringInvoiceSendType) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringSendType_${type}`);
  };
  const getAutomaticSendTypeText = (type: WintSuperkollAPIModel.InvoiceSendType) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringAutomaticSendType_${type}`);
  };
  const getStateText = (state: WintSuperkollAPIModel.RecurringInvoiceState) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringInvoiceState_${state}`);
  };

  const getSendTypes = () =>
    EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceSendType).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringSendType_${key}`),
      };
    });

  const getStates = () =>
    EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceState).map((key) => {
      return {
        id: Number(key),
        value: getStateText(key),
      };
    });

  const getStateLogs = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RecurringInvoiceClient.GetStateLogs(id)
      .then((res) => res.data);
  };

  const getFrequencyMonths = (frequency: WintSuperkollAPIModel.RecurringInvoiceFrequency) => {
    switch (frequency) {
      case WintSuperkollAPIModel.RecurringInvoiceFrequency.Monthly:
        return 1;
      case WintSuperkollAPIModel.RecurringInvoiceFrequency.Quarterly:
        return 3;
      case WintSuperkollAPIModel.RecurringInvoiceFrequency.HalfYearly:
        return 6;
      case WintSuperkollAPIModel.RecurringInvoiceFrequency.Yearly:
        return 12;
    }
  };

  return {
    get,
    filter,
    reportFilter,
    reportToAPIFilter,
    createOrUpdateInvoice,
    createInvoice,
    updateInvoice,
    getCopiedRecurringInvoice,
    start,
    stop,
    convertFormToPreviewRequest,
    convertFormToAPIRequest,
    getFrequencyMonths,
    getSendTypeText,
    getFrequencyText,
    getAutomaticSendTypeText,
    getStateText,
    getSendTypes,
    getStates,
    getStateLogs,
  };
}

export default useRecurringInvoiceService;
